import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../../../common/helpers/currency";
import type {
  RawCartTotals,
  RawShippingRate,
  RawShippingRatePackage,
} from "../../../../../../types";
import type { TransformedCartTotals } from "../../../types/cartApiTypes";

/**
 * Transforms the main cart totals object.
 * Converts net (excluding tax) values to gross (including tax) values
 * to match tax-inclusive store displays.
 */
export const transformCartTotals = (
  totals: RawCartTotals,
  shippingRatePackages: RawShippingRatePackage[],
): TransformedCartTotals => {
  let shippingStatus: "free" | "pickup" | "paid" | "none" = "none";
  let selectedShippingRate: RawShippingRate | undefined;

  const currencyInfo = extractCurrencyInfo(totals);

  // Find the selected shipping rate across all packages
  if (shippingRatePackages && shippingRatePackages.length > 0) {
    for (const pkg of shippingRatePackages) {
      selectedShippingRate = pkg.shipping_rates.find((rate) => rate.selected);
      if (selectedShippingRate) {
        break;
      }
    }
  }

  if (selectedShippingRate) {
    if (selectedShippingRate.method_id === "free_shipping") {
      shippingStatus = "free";
    } else if (selectedShippingRate.method_id === "pickup_location") {
      shippingStatus = "pickup";
    } else if (parseFloat(selectedShippingRate.price) > 0) {
      shippingStatus = "paid";
    } else {
      shippingStatus = "free";
    }
  } else {
    const hasAnyShippingMethods = shippingRatePackages?.some(
      (pkg) => pkg.shipping_rates.length > 0,
    );
    if (!hasAnyShippingMethods) {
      shippingStatus = "none";
    }
  }

  // --- SUM NET AND TAX VALUES WITHOUT CLIENT-SIDE RE-CALCULATION ---
  // To avoid introducing discrepancies with payment gateways, we do not mathematically
  // recalculate subtotals or discounts backwards from the Grand Total. We strictly trust
  // the database values provided by WooCommerce, simply adding Net + Tax where appropriate.
  const rawItemsInt = parseInt(totals.total_items || "0", 10);
  const rawItemsTaxInt = parseInt(totals.total_items_tax || "0", 10);
  const grossItemsInt = rawItemsInt + rawItemsTaxInt;

  const rawDiscountInt = parseInt(totals.total_discount || "0", 10);
  const rawDiscountTaxInt = parseInt(totals.total_discount_tax || "0", 10);
  const grossDiscountInt = rawDiscountInt + rawDiscountTaxInt;

  const rawShippingInt = parseInt(totals.total_shipping || "0", 10);
  const rawShippingTaxInt = parseInt(totals.total_shipping_tax || "0", 10);
  const grossShippingInt = rawShippingInt + rawShippingTaxInt;

  const rawFeesInt = parseInt(totals.total_fees || "0", 10);
  const rawFeesTaxInt = parseInt(totals.total_fees_tax || "0", 10);
  const grossFeesInt = rawFeesInt + rawFeesTaxInt;

  return {
    // All formatted values represent raw API data without client-side manual calculations.
    totalItems: formatPrice(String(grossItemsInt), currencyInfo),
    totalItemsNet: formatPrice(totals.total_items, currencyInfo),
    totalItemsTax: formatPrice(totals.total_items_tax, currencyInfo),

    totalFees: formatPrice(String(grossFeesInt), currencyInfo),
    totalFeesTax: formatPrice(totals.total_fees_tax, currencyInfo),

    totalDiscount: formatPrice(String(grossDiscountInt), currencyInfo),
    totalDiscountTax: formatPrice(totals.total_discount_tax, currencyInfo),

    totalShipping: formatPrice(String(grossShippingInt), currencyInfo),
    totalShippingTax: formatPrice(totals.total_shipping_tax, currencyInfo),

    // Grand total is mapped directly from the API to guarantee 100% alignment with payment gateways
    totalPrice: formatPrice(totals.total_price, currencyInfo),
    totalTax: formatPrice(totals.total_tax, currencyInfo),

    taxLines: totals.tax_lines.map((line) => ({
      ...line,
      price: formatPrice(line.price, currencyInfo),
    })),

    rawTotalPrice: totals.total_price,
    rawTotalTax: totals.total_tax,

    hasFees: grossFeesInt > 0,
    hasDiscount: grossDiscountInt > 0,
    hasTax: parseInt(totals.total_tax, 10) > 0,
    shippingStatus: shippingStatus,

    currencyMinorUnit: currencyInfo.currencyMinorUnit || 2,
    currencyDecimalSeparator: currencyInfo.currencyDecimalSeparator || ",",
    currencyThousandSeparator: currencyInfo.currencyThousandSeparator || ".",
    currencyPrefix: currencyInfo.currencyPrefix || "",
    currencySuffix: currencyInfo.currencySuffix || "",
  };
};
